import TodayRoute from '../components/schedule/today-route.tsx';
import { WorkPage } from '../components/work-page.tsx';
import { TableScroll } from '@web/components/table-scroll.tsx';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { aiApi, dashboardApi, inboundTextApi, salesDashboardApi } from '../api/endpoints.ts';
import { api } from '../api/client.ts';
import type { OfficeSwapListing } from '../components/shift-swaps.tsx';
import { useSession } from '../state/session.tsx';
import { PlanRequiredPage, companyHasPlanFeature, usePlanFeature } from '../components/plan-gate.tsx';
import { Alert, Button, LoadingBlock, classes } from '../components/ui.tsx';
import { OwedCard } from '../components/payments/owed-card.tsx';
import { BookedJobsCard } from '../components/quote/booked-jobs.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatCents } from '@shared/calculations/pricing.ts';
import { formatMinutes } from '../i18n/format.ts';
import { formatShortInstant } from '../i18n/datetime.ts';
import { quoteValueAmount } from '../components/quote/plain-price.tsx';
import { getDateLocale } from '../i18n/locale.ts';
import { planHasFeature } from '@shared/constants/index.ts';
import { DEFAULT_CURRENCY_CODE } from '@shared/currency.ts';
import { useState, type ReactNode } from 'react';
import type { SalesDashboardFigures } from '@shared/types/index.ts';

/**
 * The owner's landing page: the quote pipeline, money collected, the week's
 * workload, and the short list of things waiting on a decision. Every number
 * links to the page where it can be acted on, and uses the same predicates as
 * that page (derived expiry above all), so the two can never disagree.
 */
/**
 * The role, then the plan, decides which page this is. Somebody without
 * `reports:read` — an estimator, a read-only viewer — gets the sales
 * dashboard: quotes only, on every plan, because the owner's figures (money
 * collected, failed payments) are what that permission protects. For the
 * rest every plan has one (`quote_payments`); the full owner dashboard is
 * `reports_core`, and the Free plan gets its own short one — what it can do,
 * and what it is owed. The check is a wrapper rather than an early return
 * inside a page, so each body's hooks keep running in the same order on every
 * render.
 */
export default function DashboardPage() {
  const { t } = useTranslation();
  const { can } = useSession();
  const hasReports = usePlanFeature('reports_core');
  const hasQuotePayments = usePlanFeature('quote_payments');
  if (!can('reports:read') && can('quotes:read')) return <SalesDashboardContent />;
  if (hasReports) return <DashboardPageContent />;
  if (hasQuotePayments) return <FreeDashboardContent />;
  return (
    <>
      <PlanRequiredPage feature="reports_core" title={t('dashboard.title')} />
    </>
  );
}

/**
 * The Free plan's home: one line saying what the plan does, and what the
 * company is owed on the quotes it has won. Nothing that only explains what
 * is missing — the reports and the calendar have their own pages for that.
 */
function FreeDashboardContent() {
  const { t } = useTranslation();
  useDocumentTitle(t('dashboard.documentTitle'));
  return (
    <WorkPage title={t('dashboard.title')} description={t('freeDashboard.onboarding')}>
      <div className="mb-6 flex flex-wrap gap-2">
        <Link to="/quotes/new" className="btn btn-primary">
          {t('freeDashboard.newQuote')}
        </Link>
        <Link to="/customers" className="btn btn-secondary">
          {t('freeDashboard.customers')}
        </Link>
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        {/* The next jobs on the diary, beside what they are owed: the Free
            plan's whole picture of its work without a calendar. */}
        <BookedJobsCard />
        <OwedCard />
      </div>
    </WorkPage>
  );
}

function DashboardPageContent() {
  const { t } = useTranslation();
  useDocumentTitle(t('dashboard.documentTitle'));
  const { company, currentLocation, locations, can } = useSession();
  const hasScheduling = usePlanFeature('scheduling');

  // People assigned to a single location have no scope to choose; everyone
  // else can widen the numbers from the working location to all of theirs.
  const hasScopes = locations.length >= 2;
  const [scope, setScope] = useState<'location' | 'all'>('location');
  const effectiveScope = hasScopes ? scope : 'location';

  const query = useQuery({
    queryKey: ['dashboard', effectiveScope],
    queryFn: () => dashboardApi.get(effectiveScope === 'all' ? 'all' : undefined),
  });
  // Shift swaps waiting on the office (0095). The working location's queue —
  // swaps are approved where the visit is — whichever scope the tiles show.
  const swapApprover = can('staff_portal:manage') && companyHasPlanFeature(company, 'staff_portal');
  const swaps = useQuery({
    queryKey: ['swaps', 'waiting', currentLocation?.id],
    queryFn: () => api.get<OfficeSwapListing>('/swaps?status=pending_owner'),
    enabled: swapApprover,
    retry: false,
  });
  const swapsWaiting = swapApprover ? (swaps.data?.waitingOnOwner ?? 0) : 0;
  // A failed read is not "none waiting": say it could not be checked.
  const swapsFailed = swapApprover && swaps.isError;

  const currency = company?.currencyCode ?? DEFAULT_CURRENCY_CODE;
  const money = (cents: number) => formatCents(cents, currency, getDateLocale());

  const scopeToggle = hasScopes ? (
    <div
      role="group"
      aria-label={t('dashboard.scopeLabel')}
      className="inline-flex overflow-hidden rounded-md border border-[color:var(--color-line)]"
    >
      <ScopeButton active={scope === 'location'} onClick={() => setScope('location')}>
        {currentLocation
          ? t('dashboard.scopeThisLocationNamed', { name: currentLocation.name })
          : t('dashboard.scopeThisLocation')}
      </ScopeButton>
      <ScopeButton active={scope === 'all'} onClick={() => setScope('all')}>
        {t('dashboard.scopeAllLocations')}
      </ScopeButton>
    </div>
  ) : null;

  if (query.isLoading)
    return (
      <WorkPage title={t('dashboard.title')} description={t('dashboard.description')}>
        <div className="card">
          <LoadingBlock label={t('dashboard.loading')} />
        </div>
      </WorkPage>
    );
  if (query.isError) {
    return (
      <WorkPage title={t('dashboard.title')} description={t('dashboard.description')}>
        <div className="card">
          <Alert tone="error" title={t('dashboard.loadErrorTitle')}>
            {(query.error as Error).message}
          </Alert>
        </div>
      </WorkPage>
    );
  }
  if (!query.data) return null;

  const data = query.data;
  const decisions = data.decisions90d.accepted + data.decisions90d.declined;
  const acceptanceRate = decisions > 0 ? Math.round((data.decisions90d.accepted / decisions) * 100) : null;
  const attention = data.needsAttention;
  const accuracy = data.timeAccuracy;
  const accuracyDeltaMinutes = accuracy.actualMinutes - accuracy.scheduledMinutes;

  return (
    <WorkPage title={t('dashboard.title')} description={t('dashboard.description')} actions={scopeToggle}>
      {/* "Start my day" for anyone here who goes out on visits too.
          Renders nothing on a day with none assigned to them. */}
      {hasScheduling && can('schedule:read') && <TodayRoute compact />}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('dashboard.collected30d')}
          value={money(data.collected30dCents)}
          hint={
            data.tips30dCents > 0
              ? t('dashboard.collected30dHintWithTips', { tips: money(data.tips30dCents) })
              : t('dashboard.collected30dHint')
          }
        />
        <StatTile
          label={t('dashboard.acceptedWork')}
          value={quoteValueAmount(data.accepted, currency, t)}
          hint={t('dashboard.acceptedWorkHint', { count: data.accepted.count })}
        />
        <StatTile
          label={t('dashboard.acceptanceRate')}
          value={acceptanceRate === null ? '—' : `${acceptanceRate}%`}
          hint={
            acceptanceRate === null
              ? t('dashboard.acceptanceRateEmpty')
              : t('dashboard.acceptanceRateHint', {
                  accepted: data.decisions90d.accepted,
                  total: decisions,
                })
          }
        />
        <StatTile
          label={t('dashboard.weekAhead')}
          value={t('dashboard.weekAheadValue', { count: data.weekAhead.visits })}
          hint={t('dashboard.weekAheadHint', { time: formatMinutes(data.weekAhead.scheduledMinutes) })}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <OwedCard scope={effectiveScope} />
        <section className="card">
          <h2 className="mb-3 text-lg font-semibold">{t('dashboard.pipelineTitle')}</h2>
          {data.pipeline.every((row) => row.count === 0) ? (
            <p className="text-[color:var(--color-muted)]">{t('dashboard.pipelineEmpty')}</p>
          ) : (
            <TableScroll aria-label={t('dashboard.pipelineTitle')}>
              <table className="data-table w-full min-w-max border-collapse text-left text-sm">
                <caption className="sr-only">{t('dashboard.pipelineTitle')}</caption>
                <thead>
                  <tr className="border-b border-[color:var(--color-line)]">
                    <th scope="col" className="py-2 font-semibold">
                      {t('dashboard.pipelineStatus')}
                    </th>
                    <th scope="col" className="py-2 text-right font-semibold">
                      {t('dashboard.pipelineCount')}
                    </th>
                    <th scope="col" className="py-2 text-right font-semibold">
                      {t('dashboard.pipelineValue')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.pipeline
                    .filter((row) => row.count > 0)
                    .map((row) => (
                      <tr key={row.status} className="border-b border-[color:var(--color-line)] last:border-0">
                        <td className="py-2">
                          <Link
                            to={`/quotes?status=${row.status}`}
                            className="text-[color:var(--color-brand)] hover:underline"
                          >
                            {t(`quotes.status.${row.status}`)}
                          </Link>
                        </td>
                        <td className="tabular py-2 text-right">{row.count}</td>
                        <td className="tabular py-2 text-right">{quoteValueAmount(row, currency, t)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </section>

        <section className="card">
          <h2 className="mb-3 text-lg font-semibold">{t('dashboard.attentionTitle')}</h2>
          <ul className="space-y-2 text-sm">
            <AttentionRow
              count={attention.newQuoteRequests}
              label={t('dashboard.attentionRequests', { count: attention.newQuoteRequests })}
              to="/requests"
            />
            <AttentionRow
              count={attention.expiringSoon}
              label={t('dashboard.attentionExpiring', { count: attention.expiringSoon })}
              to="/quotes?filter=expiring"
            />
            <AttentionRow
              count={attention.changeRequested}
              label={t('dashboard.attentionChangeRequested', { count: attention.changeRequested })}
              // Where each request is answered. Like the failed-payments link
              // below, the list reads the session's location only, so
              // `scope=all` lets it say so when the count covered every one.
              to={`/schedule/change-requests${effectiveScope === 'all' ? '?scope=all' : ''}`}
            />
            <AttentionRow
              count={swapsWaiting}
              label={t('shiftSwaps.home.waiting', { count: swapsWaiting })}
              to="/staff/swaps"
            />
            {swapsFailed ? (
              <li>
                <Link to="/staff/swaps" className="text-[color:var(--color-brand)] hover:underline">
                  {t('shiftSwaps.home.failed')}
                </Link>
              </li>
            ) : null}
            <AttentionRow
              count={attention.failedPayments}
              label={t('dashboard.attentionFailedPayments', { count: attention.failedPayments })}
              // The count above can be an "all locations" aggregate, but the
              // schedule page it links to only ever reads the session's
              // current working location — there is no cross-location
              // calendar view to send it to. `scope=all` tells that page to
              // say so, rather than silently showing a shorter list than the
              // count promised.
              to={`/schedule?paymentStatus=failed${effectiveScope === 'all' ? '&scope=all' : ''}`}
            />
          </ul>
          {attention.newQuoteRequests +
            attention.expiringSoon +
            attention.changeRequested +
            attention.failedPayments +
            swapsWaiting ===
            0 && !swapsFailed ? (
            <p className="text-[color:var(--color-muted)]">{t('dashboard.attentionEmpty')}</p>
          ) : null}
        </section>
      </div>

      <section className="card mt-6">
        <h2 className="mb-2 text-lg font-semibold">{t('dashboard.accuracyTitle')}</h2>
        {accuracy.trackedVisits === 0 ? (
          <p className="text-[color:var(--color-muted)]">{t('dashboard.accuracyEmpty')}</p>
        ) : (
          <p className="text-sm">
            {t('dashboard.accuracySummary', {
              count: accuracy.trackedVisits,
              scheduled: formatMinutes(accuracy.scheduledMinutes),
              actual: formatMinutes(accuracy.actualMinutes),
            })}{' '}
            <span className="font-semibold">
              {accuracyDeltaMinutes === 0
                ? t('dashboard.accuracySpotOn')
                : accuracyDeltaMinutes > 0
                  ? t('dashboard.accuracyOver', { time: formatMinutes(accuracyDeltaMinutes) })
                  : t('dashboard.accuracyUnder', { time: formatMinutes(-accuracyDeltaMinutes) })}
            </span>
          </p>
        )}
      </section>

      {can('schedule:read') ? <InboundTextsCard /> : null}
      {can('templates:read') ? <AiSuggestionsCard /> : null}
    </WorkPage>
  );
}

/**
 * The estimator's home: the quotes they wrote, or everyone's at the location,
 * and what to do next about them. Every card opens the quotes list filtered
 * the same way it counted. A read-only viewer sees the same page without the
 * button that writes anything.
 */
function SalesDashboardContent() {
  const { t } = useTranslation();
  useDocumentTitle(t('dashboard.documentTitle'));
  const { currentLocation, can } = useSession();
  // Somebody who cannot write quotes has none of their own: a read-only
  // viewer starts on the location's. Derived on every render until the
  // person picks one, so a role that changes while the page is open (or a
  // cached session the server then corrects) moves the default with it.
  const [chosenScope, setScope] = useState<'mine' | 'location' | null>(null);
  const scope = chosenScope ?? (can('quotes:write') ? 'mine' : 'location');
  // Lead capture is a plan feature; without it /requests can only explain that.
  const hasOnlineIntake = usePlanFeature('online_intake');
  const hasScheduling = usePlanFeature('scheduling');
  const query = useQuery({
    // Keyed on the location: the figures are the working location's.
    queryKey: ['sales-dashboard', currentLocation?.id],
    queryFn: () => salesDashboardApi.get(),
  });
  const description = t('salesDashboard.description');
  const actions = (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="group"
        aria-label={t('salesDashboard.scopeLabel')}
        className="inline-flex overflow-hidden rounded-md border border-[color:var(--color-line)]"
      >
        <ScopeButton active={scope === 'mine'} onClick={() => setScope('mine')}>
          {t('salesDashboard.scopeMine')}
        </ScopeButton>
        <ScopeButton active={scope === 'location'} onClick={() => setScope('location')}>
          {currentLocation
            ? t('salesDashboard.scopeLocation', { name: currentLocation.name })
            : t('salesDashboard.scopeLocationUnnamed')}
        </ScopeButton>
      </div>
      {can('quotes:write') ? (
        <Link to="/quotes/new" className="btn btn-primary">
          {t('freeDashboard.newQuote')}
        </Link>
      ) : null}
    </div>
  );

  if (query.isLoading)
    return (
      <WorkPage title={t('dashboard.title')} description={description}>
        <div className="card">
          <LoadingBlock label={t('dashboard.loading')} />
        </div>
      </WorkPage>
    );
  if (query.isError) {
    return (
      <WorkPage title={t('dashboard.title')} description={description}>
        <div className="card">
          <Alert tone="error" title={t('dashboard.loadErrorTitle')}>
            {(query.error as Error).message}
          </Alert>
        </div>
      </WorkPage>
    );
  }
  if (!query.data) return null;

  const data = query.data;
  const money = (cents: number) => formatCents(cents, data.currencyCode, getDateLocale());
  const figures: SalesDashboardFigures = data[scope];
  const winRate = (decided: SalesDashboardFigures['decisions90d']) => {
    const total = decided.accepted + decided.declined;
    return total > 0 ? Math.round((decided.accepted / total) * 100) : null;
  };
  const rate = winRate(figures.decisions90d);
  const locationRate = winRate(data.location.decisions90d);
  const decided = figures.decisions90d.accepted + figures.decisions90d.declined;
  const sent = figures.sentThisMonth;
  // The quotes list narrowed the same way the figure was counted.
  const listOf = (filter: string) => `/quotes?${filter}${scope === 'mine' ? '&mine=1' : ''}`;
  const open = figures.open;
  const ages = [
    { key: 'age0to3', count: open.ages.days0to3 },
    { key: 'age4to7', count: open.ages.days4to7 },
    { key: 'age8to14', count: open.ages.days8to14 },
    { key: 'age15plus', count: open.ages.days15plus },
  ] as const;

  return (
    <WorkPage title={t('dashboard.title')} description={description} actions={actions}>
      {hasScheduling && can('schedule:read') && <TodayRoute compact />}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('salesDashboard.sentThisMonth')}
          value={String(sent.count)}
          hint={
            sent.count === 0
              ? t('salesDashboard.sentThisMonthEmpty')
              : t('salesDashboard.sentThisMonthHint', {
                  value: quoteValueAmount(sent, data.currencyCode, t),
                  // Over the quotes the total covers: one priced in a currency
                  // the company has since left is counted, not summed.
                  average:
                    sent.pricedCount > 0
                      ? quoteValueAmount(
                          {
                            totalCents: Math.round(sent.totalCents / sent.pricedCount),
                            lowCents: Math.round(sent.lowCents / sent.pricedCount),
                            highCents: Math.round(sent.highCents / sent.pricedCount),
                          },
                          data.currencyCode,
                          t,
                        )
                      : '—',
                })
          }
        />
        <StatTile
          label={t('salesDashboard.winRate')}
          value={rate === null ? '—' : `${rate}%`}
          hint={
            <>
              {rate === null
                ? t('salesDashboard.winRateEmpty')
                : t('salesDashboard.winRateHint', { accepted: figures.decisions90d.accepted, total: decided })}
              {scope === 'mine' && locationRate !== null ? (
                <>
                  <br />
                  {t('salesDashboard.winRateLocation', { rate: `${locationRate}%` })}
                </>
              ) : null}
            </>
          }
        />
        <Link
          to={listOf('filter=expiring')}
          className="block rounded-lg hover:ring-2 hover:ring-[color:var(--color-brand-soft)]"
        >
          <StatTile
            label={t('salesDashboard.expiring')}
            value={String(figures.expiringSoon.count)}
            hint={t('salesDashboard.expiringHint', {
              value: quoteValueAmount(figures.expiringSoon, data.currencyCode, t),
            })}
          />
        </Link>
        <Link
          to="/quotes?status=accepted"
          className="block rounded-lg hover:ring-2 hover:ring-[color:var(--color-brand-soft)]"
        >
          <StatTile
            label={t('salesDashboard.readyToBook')}
            value={String(data.readyToBook.count)}
            hint={t('salesDashboard.readyToBookHint', {
              value: quoteValueAmount(data.readyToBook, data.currencyCode, t),
            })}
          />
        </Link>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="card">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-semibold">{t('salesDashboard.openTitle')}</h2>
            <Link to={listOf('status=sent,viewed')} className="text-sm text-[color:var(--color-brand)] hover:underline">
              {t('salesDashboard.seeAll')}
            </Link>
          </div>
          {open.count === 0 ? (
            <p className="text-[color:var(--color-muted)]">{t('salesDashboard.openEmpty')}</p>
          ) : (
            <>
              <p className="mb-3 text-sm text-[color:var(--color-muted)]">
                {t('salesDashboard.openSummary', {
                  count: open.count,
                  value: quoteValueAmount(open, data.currencyCode, t),
                })}
              </p>
              <dl aria-label={t('salesDashboard.ages')} className="mb-4 grid grid-cols-4 gap-2 text-center">
                {ages.map((age) => (
                  <div key={age.key} className="rounded-md bg-[color:var(--color-surface)] p-2">
                    <dt className="text-xs text-[color:var(--color-muted)]">{t(`salesDashboard.${age.key}`)}</dt>
                    <dd className="tabular text-lg font-semibold">{age.count}</dd>
                  </div>
                ))}
              </dl>
              <h3 className="mb-2 text-sm font-semibold">{t('salesDashboard.oldestTitle')}</h3>
              <ul className="space-y-2 text-sm">
                {open.oldest.map((quote) => (
                  <li key={quote.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <Link
                      to={`/quotes/${quote.id}`}
                      className="min-w-0 text-[color:var(--color-brand)] hover:underline"
                    >
                      {quote.quoteNumber} · {quote.customerName}
                    </Link>
                    <span className="tabular text-[color:var(--color-muted)]">
                      {quoteValueAmount(quote, quote.currencyCode, t)} ·{' '}
                      {t('salesDashboard.sentOn', { date: formatShortInstant(quote.sentAt) })}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </section>

        <section className="card">
          <h2 className="mb-3 text-lg font-semibold">{t('salesDashboard.answerTitle')}</h2>
          <ul className="space-y-2 text-sm">
            <AttentionRow
              count={hasOnlineIntake ? data.newRequests : 0}
              label={t('salesDashboard.newRequests', { count: data.newRequests })}
              to="/requests"
            />
            <AttentionRow
              count={figures.followUpsDue}
              label={t('salesDashboard.followUps', { count: figures.followUpsDue })}
              to={listOf('status=viewed')}
            />
          </ul>
          {(hasOnlineIntake ? data.newRequests : 0) + figures.followUpsDue === 0 ? (
            <p className="text-[color:var(--color-muted)]">{t('salesDashboard.answerEmpty')}</p>
          ) : null}
        </section>
      </div>

      {can('schedule:read') ? <InboundTextsCard /> : null}
      {can('templates:read') ? <AiSuggestionsCard /> : null}
    </WorkPage>
  );
}

/**
 * The SMS inbox: customer texts the webhook filed, triaged by intent. Shown
 * only while something is waiting — a permanent empty card would just be
 * furniture — and every action here records a decision; changing a visit
 * still happens on the schedule page, by a person.
 */
function InboundTextsCard() {
  const { t } = useTranslation();
  const { can, company } = useSession();
  const queryClient = useQueryClient();
  // Starter has no `sms` feature, so the inbox endpoint is locked and would
  // only ever answer 403 — asking is pointless, not just slow.
  const hasSms = company !== null && planHasFeature(company.effectivePlan, 'sms');
  const query = useQuery({
    queryKey: ['inbound-texts', 'new'],
    queryFn: () => inboundTextApi.list({ status: 'new', pageSize: 5 }),
    enabled: hasSms,
    retry: false,
  });
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'handle' | 'dismiss' }) =>
      action === 'handle' ? inboundTextApi.handle(id) : inboundTextApi.dismiss(id),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['inbound-texts'] }),
  });

  const rows = query.data?.items ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="card mt-6" aria-labelledby="inbound-texts-heading">
      <h2 id="inbound-texts-heading" className="mb-1 text-lg font-semibold">
        {t('dashboard.inboundTextsTitle')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('dashboard.inboundTextsHint')}</p>
      <ul className="space-y-3">
        {rows.map((text) => (
          <li key={text.id} className="rounded border border-[color:var(--color-line)] p-3 text-sm">
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {text.intent ? (
                <span className="badge border-[color:var(--color-brand)] text-[color:var(--color-brand)]">
                  {t(`dashboard.intent.${text.intent}`)}
                </span>
              ) : null}
              <span className="font-medium">
                {text.customerId && text.customerName ? (
                  <Link to={`/customers/${text.customerId}`} className="hover:underline">
                    {text.customerName}
                  </Link>
                ) : (
                  text.fromPhone
                )}
              </span>
              <span className="text-xs text-[color:var(--color-muted)]">
                {new Date(text.createdAt).toLocaleString(getDateLocale())}
              </span>
            </div>
            <p className="mb-1 whitespace-pre-wrap">{text.body}</p>
            {text.aiSummary ? <p className="mb-2 text-xs text-[color:var(--color-muted)]">{text.aiSummary}</p> : null}
            {can('schedule:write') ? (
              <div className="flex flex-wrap gap-2">
                {text.appointmentId ? (
                  <Link to="/schedule" className="btn btn-secondary">
                    {t('dashboard.inboundTextOpenSchedule')}
                  </Link>
                ) : null}
                <Button
                  busy={decide.isPending && decide.variables?.id === text.id && decide.variables.action === 'handle'}
                  onClick={() => decide.mutate({ id: text.id, action: 'handle' })}
                >
                  {t('dashboard.inboundTextHandled')}
                </Button>
                <Button
                  busy={decide.isPending && decide.variables?.id === text.id && decide.variables.action === 'dismiss'}
                  onClick={() => decide.mutate({ id: text.id, action: 'dismiss' })}
                >
                  {t('dashboard.inboundTextDismiss')}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Open tuning suggestions from the estimated-vs-actual analysis. Accepting
 * changes ONE task's default minutes in the library; existing quotes keep
 * their snapshots either way, and the card says so.
 */
function AiSuggestionsCard() {
  const { t } = useTranslation();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: ['ai-suggestions', 'open'],
    queryFn: () => aiApi.suggestions({ status: 'open', pageSize: 10 }),
    retry: false,
  });
  const decide = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'accept' | 'dismiss' }) =>
      action === 'accept' ? aiApi.acceptSuggestion(id) : aiApi.dismissSuggestion(id),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: ['ai-suggestions'] }),
  });

  const rows = query.data?.items ?? [];
  if (rows.length === 0) return null;

  return (
    <section className="card mt-6" aria-labelledby="ai-suggestions-heading">
      <h2 id="ai-suggestions-heading" className="mb-1 text-lg font-semibold">
        {t('dashboard.aiSuggestionsTitle')}
      </h2>
      <p className="mb-3 text-sm text-[color:var(--color-muted)]">{t('dashboard.aiSuggestionsHint')}</p>
      <ul className="space-y-3">
        {rows.map((suggestion) => (
          <li key={suggestion.id} className="rounded border border-[color:var(--color-line)] p-3 text-sm">
            <p className="mb-1 font-medium">
              {t('dashboard.aiSuggestionChange', {
                task: suggestion.serviceTemplateName ?? t('dashboard.aiSuggestionUnknownTask'),
                room: suggestion.roomTemplateName ?? '—',
                from: suggestion.currentMinutes ?? 0,
                to: suggestion.suggestedMinutes ?? 0,
              })}
            </p>
            <p className="mb-2 text-[color:var(--color-muted)]">{suggestion.rationale}</p>
            {can('templates:write') ? (
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  busy={
                    decide.isPending && decide.variables?.id === suggestion.id && decide.variables.action === 'accept'
                  }
                  onClick={() => decide.mutate({ id: suggestion.id, action: 'accept' })}
                >
                  {t('dashboard.aiSuggestionAccept')}
                </Button>
                <Button
                  busy={
                    decide.isPending && decide.variables?.id === suggestion.id && decide.variables.action === 'dismiss'
                  }
                  onClick={() => decide.mutate({ id: suggestion.id, action: 'dismiss' })}
                >
                  {t('dashboard.aiSuggestionDismiss')}
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function ScopeButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={classes(
        'px-3 py-1.5 text-sm font-medium',
        active
          ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
          : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]',
      )}
    >
      {children}
    </button>
  );
}

function StatTile({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <div className="card">
      <p className="text-sm font-medium text-[color:var(--color-muted)]">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular">{value}</p>
      {hint ? <p className="mt-1 text-sm text-[color:var(--color-muted)]">{hint}</p> : null}
    </div>
  );
}

function AttentionRow({ count, label, to }: { count: number; label: string; to: string }) {
  if (count === 0) return null;
  return (
    <li>
      {/* flex-nowrap: the phone layout wraps every `#main .flex`, which would
          drop a label too long for one line below its badge. */}
      <Link to={to} className="flex flex-nowrap items-center gap-2 text-[color:var(--color-brand)] hover:underline">
        <span className="badge shrink-0 border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
          {count}
        </span>
        <span className="min-w-0">{label}</span>
      </Link>
    </li>
  );
}
